/**
 * One in-flight request per queue dialog (review C N2). A dismissed or
 * superseded request is cancelled in main and recorded ONCE as `cancelled`;
 * its late answer is then ignored — no UI state, no second analytics event.
 */
import { useCallback, useRef } from 'react';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import type { QueueRequestHandle } from './queueApi';
import type { QueueActionSource } from './queueDialogs';
import { trackQueueAction } from './queueMessageActions';

type TrackedAction = Parameters<typeof trackQueueAction>[1];
type Operation = { handle: QueueRequestHandle<unknown>; source: QueueActionSource };

export function useQueueDialogOperation(action: TrackedAction) {
  const { track } = useAnalytics();
  const active = useRef<Operation | null>(null);
  const begin = useCallback((handle: QueueRequestHandle<unknown>, source: QueueActionSource) => {
    active.current = { handle, source };
  }, []);
  /** True when this answer still belongs to the open dialog (the caller then applies and tracks it). */
  const settle = useCallback((handle: QueueRequestHandle<unknown>): boolean => {
    if (active.current?.handle !== handle) return false;
    active.current = null;
    return true;
  }, []);
  const abandon = useCallback(() => {
    const operation = active.current;
    if (!operation) return;
    active.current = null;
    operation.handle.cancel();
    trackQueueAction(track, action, 'cancelled', operation.source);
  }, [action, track]);
  return { begin, settle, abandon, track };
}
